import { notification as antdNotification } from 'antd';
import React, { useState } from 'react';
import { Danger, Info, CircleCheck, Error, Close, Lightbulb, Spinner, CloseLarge } from '@nackle/origami-icons';
import { Button } from '../../exports';

const getIcon = ( type ) => {
    switch ( type ) {
        case 'info':
            return <Info className="ant-notification-info-icon ant-notification-icon" />;
        case 'success':
            return <CircleCheck className="ant-notification-success-icon ant-notification-icon" />;
        case 'warning':
            return <Danger className="ant-notification-warning-icon ant-notification-icon" />;
        case 'error':
            return <Error className="ant-notification-error-icon ant-notification-icon" />;
        case 'tip':
            return <Lightbulb className="ant-notification-tip-icon ant-notification-icon" />;
        case 'loading':
            return <Spinner className="ant-notification-loading-icon ant-notification-icon" />;
        default:
            return;
    }
};

export const notification = {
    // static methods

    /**
     * A notification
     * @module Notification.open
     * @param {Object} config - the notification config
     * @param {string} [config.size] - the notification size
     * @param {string} [config.type] - the type of notification
     * @param {number} [config.duration] - the amount of time the notification will display
     * @param {string} [config.message] - the message that will display in the notification
     * @param {string} [config.description] - the description displayed in the notification
     * @param {React.ReactElement} [config.closeIcon] - the icon displayed for the close button
     * @returns {React.ReactElement} - notification
    **/
    open: function ( config ) {
        antdNotification.open( {
            className: config.size === 'large' ? 'ant-notification-large' : config.className,
            type: config.type === 'tip' || config.type === 'loading' ? 'info' : config.type, // antd doesn't have a tip or loading type, overwrite what we pass to it but use custom icon
            icon: getIcon( config.type ),
            duration: config.duration,
            message: <>
                <div>{ config.message }</div>
            </>,
            closeIcon: <Button type='text' shape='circle' icon={ config.size === 'large' ? <CloseLarge/> : <Close/> }></Button>,
            description: config.description,
            ...config
        } );
    },

    /**
     * an info notification
     * @module Notification.info
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - an info notification
    **/
    info: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'info'
        } );
    },

    /**
     * a success notification
     * @module Notification.success
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - a success notification
    **/
    success: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'success'
        } );
    },

    /**
     * a warning notification
     * @module Notification.warning
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - a warning notification
    **/
    warning: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'warning'
        } );
    },

    /**
     * an error notification
     * @module Notification.error
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - an error notification
    **/
    error: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'error'
        } );
    },

    /**
     * a tip notification
     * @module Notification.tip
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - a tip notification
    **/
    tip: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'tip'
        } );
    },

    /**
     * a loading notification
     * @module Notification.loading
     * @param {string} message - the notification message
     * @param {string} description - the notification description
     * @param {number} duration - the amount of time the notification will display
     * @param {Function} onClose - the callback method that executes when the notification is closed
     * @returns {React.ReactElement} - a loading notification
    **/
    loading: function ( message, description, duration, onClose ) {
        this.open( {
            message,
            description,
            duration,
            onClose,
            type: 'loading'
        } );
    },

    // Callback methods
    useNotification: () => {
        const [ notificationApi, contextHolder ] = antdNotification.useNotification();

        /**
         * A notification
         * @module Notification.open
         * @param {Object} config - the notification config
         * @param {string} [config.size] - the notification size
         * @param {string} [config.type] - the type of notification
         * @param {number} [config.duration] - the amount of time the notification will display
         * @param {string} [config.message] - the message that will display in the notification
         * @param {string} [config.description] - the description displayed in the notification
         * @param {React.ReactElement} [config.closeIcon] - the icon displayed for the close button
         * @returns {React.ReactElement} - notification
        **/
        const open = ( config ) => {
            notificationApi.open( {
                className: config.size === 'large' ? 'ant-notification-large' : config.className,
                type: config.type === 'tip' ? 'info' : config.type, // antd doesn't have a tip type, overwrite what we pass to it but use custom icon
                icon: getIcon( config.type ),
                duration: config.duration,
                message: <>
                    <div>{ config.message }</div>
                </>,
                closeIcon: <Button type='text' shape='circle' icon={ config.size === 'large' ? <CloseLarge/> : <Close/> }></Button>,
                description: config.description,
                ...config
            } );
        };

        /**
         * an info notification
         * @module Notification.info
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - an info notification
        **/
        const info = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'info'
            } );
        };

        /**
         * a success notification
         * @module Notification.success
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - a success notification
        **/
        const success = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'success'
            } );
        };

        /**
         * a warning notification
         * @module Notification.warning
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - a warning notification
        **/
        const warning = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'warning'
            } );
        };

        /**
         * an error notification
         * @module Notification.error
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - an error notification
        **/
        const error = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'error'
            } );
        };

        /**
         * a tip notification
         * @module Notification.tip
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - a tip notification
        **/
        const tip = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'tip'
            } );
        };

        /**
         * a loading notification
         * @module Notification.loading
         * @param {string} message - the notification message
         * @param {string} description - the notification description
         * @param {number} duration - the amount of time the notification will display
         * @param {Function} onClose - the callback method that executes when the notification is closed
         * @returns {React.ReactElement} - a loading notification
        **/
        const loading = ( message, description, duration, onClose ) => {
            open( {
                message,
                description,
                duration,
                onClose,
                type: 'loading'
            } );
        };
        // we need to insure that the methods keep the same reference between rerenders so we set as state variable.
        // unnecessary rerenders and side effects can happen otherwise.  
        const [ api ] = useState( { open, info, success, warning, error, tip, loading } );

        return [ api, contextHolder ];
    }
};

